<script setup>
import { ref, computed } from 'vue'
import { NButton } from 'naive-ui'
import { 
  initiateGoogleOAuth, 
  initiateMicrosoftOAuth, 
  initiateGitHubOAuth,
  initiateYahooOAuth,
  initiateAppleOAuth
} from '@/services/oauth'
import { dt } from '@/utils/i18n';

// Import icons
import GoogleIcon from './icons/GoogleIcon.vue'
import MicrosoftIcon from './icons/MicrosoftIcon.vue'
import GitHubIcon from './icons/GithubIcon.vue'
import YahooIcon from './icons/YahooIcon.vue'
import AppleIcon from './icons/AppleIcon.vue';

const props = defineProps({
  provider: {
    type: String,
    required: true,
    validator: (value) => ['google', 'microsoft', 'github', 'yahoo', 'apple'].includes(value)
  },
  buttonText: {
    type: String,
    default: ''
  },
  role: {
    type: String,
    default: 'client'
  },
  language: {
    type: String,
    default: 'english'
  }
})

const emit = defineEmits(['success', 'error'])
const loading = ref(false)

// Map provider names to their respective icons
const providerIcon = computed(() => {
  const iconMap = {
    'google': GoogleIcon,
    'microsoft': MicrosoftIcon,
    'github': GitHubIcon,
    'yahoo': YahooIcon,
    'apple': AppleIcon,
  }
  return iconMap[props.provider]
})

// Determine button text
const buttonText = computed(() => {
  if (props.buttonText) {
    return props.buttonText
  }
  //  return `${dt('register.oauthLabel')} ${props.provider.charAt(0).toUpperCase() + props.provider.slice(1)}`
  return `Continue with ${props.provider.charAt(0).toUpperCase() + props.provider.slice(1)}`
})

// Function to initiate OAuth
async function initiateOAuth() {

  loading.value = true
  
  try {
    // Store role and language in localStorage
    localStorage.setItem('oauth_pending_role', props.role)
    localStorage.setItem('oauth_pending_language', props.language)
    
    // Call the appropriate OAuth function based on provider
    let result
    switch (props.provider) {
      case 'google':
        result = await initiateGoogleOAuth(props.role)
        break
      case 'microsoft':
        result = await initiateMicrosoftOAuth(props.role)
        break
      case 'github':
        result = await initiateGitHubOAuth(props.role)
        break
      case 'yahoo':
        result = await initiateYahooOAuth(props.role)
        break
      case 'apple':
        result = await initiateAppleOAuth(props.role)
        break
    }
    
    emit('success', result)
  } catch (error) {
    console.error(`OAuth error with ${props.provider}:`, error)
    emit('error', error)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <NButton
    :class="['oauth-button', `oauth-button-${provider}`]" 
    @click="initiateOAuth"
    :loading="loading"
    :disabled="loading">
    <template #icon>
      <component :is="providerIcon" v-if="providerIcon" />
    </template>
    {{ buttonText }}
  </NButton>
</template>

<style scoped>
.oauth-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: 8px 0;
}

/* Provider-specific styling */
.oauth-button-google {
  background-color: #4285F4;
  color: white;
}

.oauth-button-microsoft {
  background-color: #2F2F2F;
  color: white;
}

.oauth-button-github {
  background-color: #24292e;
  color: white;
}

.oauth-button-yahoo {
  background-color: #720e9e;
  color: white;
}
</style>